import React from "react";

import { t } from "src/utils/i18nText";
import { reloadIfStaleBuild } from "src/utils/staleBuild";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
};

/**
 * Глобальный перехватчик ошибок рендера. Без него любое необработанное
 * исключение в дереве компонентов роняет весь UI в «белый экран». Здесь же
 * показываем понятный фолбэк с возвратом на главную.
 *
 * Намеренно самодостаточен: только inline-стили, без redux/CSS-переменных темы
 * и без react-intl-контекста — чтобы фолбэк отрисовался даже если источник
 * падения именно в них. Тексты берём напрямую из словаря (src/utils/i18nText).
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Вкладка, открытая до выката новой версии, не может догрузить страницу из
    // старой сборки. Это чинится перезагрузкой — делаем её сами, вместо того
    // чтобы показывать пользователю ошибку.
    if (reloadIfStaleBuild(error)) {
      return;
    }

    // Лог для диагностики причины конкретного падения.
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary поймал ошибку рендера:", error, info);
  }

  handleReset = () => {
    // Возврат на главную с полной перезагрузкой — гарантированно выводит
    // приложение из сломанного состояния.
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          padding: 24,
          textAlign: "center",
          fontFamily: "GolosText-Regular, Arial, sans-serif",
          color: "#222023",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>
          {t("components.organisms.ErrorBoundary.title")}
        </h2>
        <p style={{ margin: 0, maxWidth: 360, color: "#555", lineHeight: 1.5 }}>
          {t("components.organisms.ErrorBoundary.description")}
        </p>
        <button
          type="button"
          onClick={this.handleReset}
          style={{
            marginTop: 8,
            padding: "12px 24px",
            border: "none",
            borderRadius: 12,
            background: "#7747E5",
            color: "#fff",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {t("components.organisms.ErrorBoundary.button")}
        </button>
      </div>
    );
  }
}
